/**
 * プレイヤーアニメーション計算モジュール
 *
 * 登場演出、ダメージモーション、特殊演出など
 * プレイヤーに関連するアニメーションロジックを管理
 *
 * @module features/player/animations/player-animations
 */

import type p5 from 'p5';

/**
 * 登場演出の進行状態
 */
export interface EntryAnimationState {
    /** X座標 */
    x: number;
    /** Y座標 */
    y: number;
    /** ブースターの透明度 (0-255) */
    boosterAlpha: number;
    /** ブースターパーティクルをクリアすべきかどうか */
    shouldClearBooster: boolean;
}

/**
 * calculateEntryAnimation() - 登場演出のアニメーション計算
 *
 * 進行度に基づいて座標とブースター強度を計算します。
 * Entry状態から呼び出され、6段階のフェーズで構成されます。
 *
 * フェーズ構成:
 * - Phase 1a (0% ~ 15%): 加速フェーズ - 画面外から加速開始
 * - Phase 1b (15% ~ 30%): 巡航フェーズ - 等速で上昇
 * - Phase 2 (30% ~ 55%): 減速フェーズ - 緩やかに減速してmidYで停止、ブースターフェード
 * - Phase 2b (55% ~ 75%): 中央から定位置へ下降（ブースターなし）
 * - Phase 3 (75% ~ 90%): 右へスライド
 * - Phase 4 (90% ~ 95%): 左へスライド
 * - Phase 5 (95% ~ 100%): 左で停止
 *
 * @param progress - 演出の進行度 (0.0 ~ 1.0)
 * @param centerX - 画面中央X座標
 * @param lanes - レーン配列
 * @param startY - 開始Y座標（画面外）
 * @param midY - 中間Y座標（画面中央付近）
 * @param endY - 終了Y座標（プレイヤーの定位置）
 * @param p - P5インスタンス
 * @returns アニメーション状態
 */
export function calculateEntryAnimation(
    progress: number,
    centerX: number,
    lanes: number[],
    startY: number,
    midY: number,
    endY: number,
    p: p5
): EntryAnimationState {

    // 全体の移動距離を計算（startY → midY）
    const totalDistance = startY - midY;
    // 各フェーズの移動割合（加速20% + 巡航50% + 減速30% = 100%）
    const accelRatio = 0.20;   // 加速フェーズで20%移動
    const cruiseRatio = 0.50;  // 巡航フェーズで50%移動

    // Phase 1a: 加速フェーズ（0% ~ 15%）- 徐々に速度を上げる
    if (progress < 0.15) {
        const phaseProgress = progress / 0.15;
        // easeInQuad: 加速感
        const easeProgress = phaseProgress * phaseProgress;
        const targetY = startY - totalDistance * accelRatio;
        const y = p.lerp(startY, targetY, easeProgress);

        return {
            x: centerX,
            y: y,
            boosterAlpha: 255,
            shouldClearBooster: false
        };
    }

    // Phase 1b: 巡航フェーズ（15% ~ 30%）- 等速で上昇
    if (progress < 0.30) {
        const phaseProgress = (progress - 0.15) / 0.15;
        const startYPhase = startY - totalDistance * accelRatio;
        const targetY = startY - totalDistance * (accelRatio + cruiseRatio);
        // 線形補間：等速移動
        const y = p.lerp(startYPhase, targetY, phaseProgress);

        return {
            x: centerX,
            y: y,
            boosterAlpha: 255,
            shouldClearBooster: false
        };
    }

    // Phase 2: 減速フェーズ（30% ~ 55%）- 徐々に減速してmidYで停止、ブースターフェード
    if (progress < 0.55) {
        const phaseProgress = (progress - 0.30) / 0.25;
        // easeOutQuart: より緩やかな減速（停止直前がスムーズ）
        const easeProgress = 1 - p.pow(1 - phaseProgress, 4);
        const startYPhase = startY - totalDistance * (accelRatio + cruiseRatio);
        const y = p.lerp(startYPhase, midY, easeProgress);
        // ブースターは減速に合わせてフェードアウト（より緩やかに）
        const alpha = 255 * p.pow(1 - phaseProgress, 3);

        return {
            x: centerX,
            y: y,
            boosterAlpha: alpha,
            shouldClearBooster: false
        };
    }

    // Phase 2b: 中央から定位置へ下降（55% ~ 75%）- ブースターなし
    if (progress < 0.75) {
        const phaseProgress = (progress - 0.55) / 0.20;
        // easeInQuad: 加速しながら下降（重力感）
        const easeProgress = phaseProgress * phaseProgress;
        const y = p.lerp(midY, endY, easeProgress);

        return {
            x: centerX,
            y: y,
            boosterAlpha: 0,
            shouldClearBooster: true
        };
    }

    // Phase 3: 右へスライド（75% ~ 90%）
    if (progress < 0.90) {
        const phaseProgress = (progress - 0.75) / 0.15;
        const easeProgress = p.sin(phaseProgress * p.HALF_PI);
        const x = p.lerp(centerX, lanes[1], easeProgress);

        return {
            x: x,
            y: endY,
            boosterAlpha: 0,
            shouldClearBooster: false
        };
    }

    // Phase 4: 左へスライド（90% ~ 95%）
    if (progress < 0.95) {
        const phaseProgress = (progress - 0.90) / 0.05;
        const easeProgress = p.sin(phaseProgress * p.HALF_PI);
        const x = p.lerp(lanes[1], lanes[0], easeProgress);

        return {
            x: x,
            y: endY,
            boosterAlpha: 0,
            shouldClearBooster: false
        };
    }

    // Phase 5: 左で停止
    return {
        x: lanes[0],
        y: endY,
        boosterAlpha: 0,
        shouldClearBooster: false
    };
}
